import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Label } from '../../../../../../components/ui/label';
import { Layers, Trash2 } from 'lucide-react';
import { DashboardProject } from '../../../../types/project';
import type { PipeSize1, SetupState1, FabricationSetupData, SpoolSetupDetailRow } from '../../../../types/setup';
import {
  buildSpoolDetailsPayload,
  defaultSpoolDisplayName,
  getNumberOfSpoolsFromPipe,
  getSpoolDetailsFromPipe,
  normalizeSpoolDetailsRows,
  withSyncedSpoolCounts,
} from '../../../../types/setup';
import { CommonSetupForm } from './CommonSetupForm';
import { NumberInput } from '../../../../../../components/ui/NumberInput';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from '../../../../../../components/ui/dialog';
import { Button } from '../../../../../../components/ui/button';
import { Input } from '../../../../../../components/ui/input';
import { SearchableSelect } from '../../../../../../components/ui/SearchableSelect';
import AddNewItemDialog from '../../../../../../components/ui/AddNewItemDialog';
import { Notification as customNotification } from '../../../../../../components/ui/custom-notification';
import { AddMaterialCategory, getListMaterialCategory } from '../../../../../../redux/Project/materialSaga';

const SPOOL_LENGTH_MEASUREMENT_TAGS_TYPE = 'SPOOL_LENGTH_MEASUREMENT_TYPE';

const DEFAULT_MEASURE_TYPE_NAMES = ['N/A'] as const;

function buildMeasureTypeSelectOptions(
  pipe: PipeSize1,
  apiTags: { tags_name?: string; tags_type?: string }[]
): { id: string; name: string }[] {
  const seen = new Set<string>(
    DEFAULT_MEASURE_TYPE_NAMES.map((n) => n.toLowerCase())
  );
  const extras: string[] = [];

  const pushName = (raw: string) => {
    const m = typeof raw === 'string' ? raw.trim() : '';
    if (!m) return;
    const key = m.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    extras.push(m);
  };

  for (const t of apiTags) {
    pushName(String(t.tags_name ?? ''));
  }

  for (const r of getSpoolDetailsFromPipe(pipe)) {
    pushName(typeof r.measureType === 'string' ? r.measureType : '');
  }

  extras.sort((a, b) => a.localeCompare(b));
  return [
    ...DEFAULT_MEASURE_TYPE_NAMES.map((name) => ({ id: name, name })),
    ...extras.map((name) => ({ id: name, name })),
  ];
}

interface SpoolsSetupProps {
  project: DashboardProject;
  manufacturingList?: any[];
  setManufacturingList: React.Dispatch<React.SetStateAction<any[]>>;
  unit: string;
  setUnit?: (unit: string) => void;
  showValidation?: boolean;
  setupData?: FabricationSetupData;
  state?: SetupState1;
  setState?: React.Dispatch<React.SetStateAction<SetupState1>>;
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  canEdit?: boolean;
  onSavePipe?: (pipeIndex: number) => Promise<boolean>;
}

export interface SpoolsSetupRef {
  getSetupData: () => SetupState1;
}

function spoolLengthAsNumberInputValue(raw: unknown): string | number {
  if (raw === '' || raw == null) return '';
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  return Number.isFinite(n) ? n : '';
}

export const SpoolsSetup = forwardRef<SpoolsSetupRef, SpoolsSetupProps>(
  (
    {
      project,
      manufacturingList,
      setManufacturingList,
      unit,
      setUnit,
      showValidation = false,
      setupData,
      state: propState,
      setState: propSetState,
      setHasUnsavedChanges,
      canEdit = true,
      onSavePipe,
    },
    ref
  ) => {
    const [internalState, setInternalState] = useState<SetupState1>({
      numberOfPipeSizes: 1,
      projectId: parseInt(project.project.projectId),
      projectTypeId: project.project.projectTypes.fabrication?.options?.spools?.project_type_id || 0,
      projectType: 'fabrication',
      projectSubType: 'spools',
      pipeSizes: [
        {
          pipeId: 0,
          pipeName: 'Pipe 1',
          pipeSize: 0,
          wallThickness: 0,
          numberOfBuckleArrestors: 0,
          numberOfSpools: 0,
          numberOfPipes: 0,
          pipeLength: 0,
          numberOfForges: 0,
          outputAssemblyLength: 0,
          manufacturerId: 0,
          manufacturerIds: [],
          pipeOrder: 1,
          isActive: true,
          prefixAssemblyName: 'Assy',
          groupId: '',
          jobNumber: {
            numberOfJobs: 1,
            jobNumbers: [''],
          },
          documentsData: {
            numberOfDocs: 0,
            isDocumentsAvailable: false,
            documentIds: [],
            documentFileNames: [],
          },
          spoolDetails: buildSpoolDetailsPayload(0, []),
        },
      ],
    });

    const state = propState || internalState;
    const setState = propSetState || setInternalState;

    const [isSpoolsModalVisible, setIsSpoolsModalVisible] = useState(false);
    const [pendingSpoolsChange, setPendingSpoolsChange] = useState<{
      value: string;
      index: number;
    } | null>(null);

    const [isAddingMeasureType, setIsAddingMeasureType] = useState(false);
    const [measureTypeAddContext, setMeasureTypeAddContext] = useState<{
      pipeIndex: number;
      rowIndex: number;
    } | null>(null);
    const [spoolLengthMeasurementTags, setSpoolLengthMeasurementTags] = useState<any[]>([]);

    useEffect(() => {
      let cancelled = false;
      const loadMeasurementTypeTags = async () => {
        try {
          const tags = await getListMaterialCategory();
          if (cancelled || !Array.isArray(tags)) return;
          const filtered = tags.filter(
            (t: any) =>
              t.tags_type === SPOOL_LENGTH_MEASUREMENT_TAGS_TYPE ||
              t.tags_type === `${SPOOL_LENGTH_MEASUREMENT_TAGS_TYPE}_TAGS` ||
              (typeof t.tags_type === 'string' &&
                t.tags_type.toLowerCase().includes('spool_length_measurement'))
          );
          setSpoolLengthMeasurementTags(filtered);
        } catch (e) {
          console.error('Failed to load spool length measurement type tags:', e);
        }
      };
      loadMeasurementTypeTags();
      return () => {
        cancelled = true;
      };
    }, [project.project.projectId]);

    useImperativeHandle(ref, () => ({
      getSetupData: () => state,
    }));

    const validateField = (value: any): boolean => {
      if (value === null || value === undefined || value === '' || value === 0) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      return true;
    };

    const handleNumberOfSpoolsChange = (value: string, index: number) => {
      if (!canEdit) return;
      if (!value) return;
      const num = parseInt(value) || 0;
      setState((prev) => ({
        ...prev,
        pipeSizes: prev.pipeSizes.map((p, i) => {
          if (i !== index) return p;
          return {
            ...withSyncedSpoolCounts(p, num),
            spoolDetails: buildSpoolDetailsPayload(
              num,
              normalizeSpoolDetailsRows(num, getSpoolDetailsFromPipe(p))
            ),
          };
        }),
      }));
      setHasUnsavedChanges(true);
    };

    const updateSpoolDetailCell = (
      pipeIndex: number,
      rowIndex: number,
      field: keyof SpoolSetupDetailRow,
      raw: string
    ) => {
      if (!canEdit) return;
      setState((prev) => ({
        ...prev,
        pipeSizes: prev.pipeSizes.map((p, i) => {
          if (i !== pipeIndex) return p;
          const count = getNumberOfSpoolsFromPipe(p);
          const rows = normalizeSpoolDetailsRows(count, getSpoolDetailsFromPipe(p));
          const next = rows.map((row, ri) => {
            if (ri !== rowIndex) return row;
            let value = raw;
            if (field === 'spoolName' && typeof raw === 'string' && raw.trim() === '') {
              value = defaultSpoolDisplayName(rowIndex);
            }
            return { ...row, [field]: value };
          });
          return { ...p, spoolDetails: buildSpoolDetailsPayload(count, next) };
        }),
      }));
      setHasUnsavedChanges(true);
    };

    const handleCancelSpoolsChange = () => {
      setIsSpoolsModalVisible(false);
      setPendingSpoolsChange(null);
    };

    const handleAddMeasureType = async (name: string) => {
      if (!canEdit || !measureTypeAddContext) return;
      const trimmed = name.trim();
      if (!trimmed) return;
      const pipe = state.pipeSizes[measureTypeAddContext.pipeIndex];
      if (!pipe) return;
      const opts = buildMeasureTypeSelectOptions(pipe, spoolLengthMeasurementTags);
      if (opts.some((o) => o.name.toLowerCase() === trimmed.toLowerCase())) {
        customNotification.warning(`"${trimmed}" is already in the list for this pipe`);
        throw new Error('duplicate');
      }
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const apiPayload = {
        tags_name: trimmed,
        project_id: Number(project.project.projectId),
        tags_type: SPOOL_LENGTH_MEASUREMENT_TAGS_TYPE,
        created_by: user.email || '',
      };
      const res = await AddMaterialCategory(apiPayload, 'Spool length measurement type');
      if (!res) {
        throw new Error('add-failed');
      }
      const label = typeof res.tags_name === 'string' && res.tags_name.trim() ? res.tags_name.trim() : trimmed;
      setSpoolLengthMeasurementTags((prev) => [...prev, res]);
      updateSpoolDetailCell(
        measureTypeAddContext.pipeIndex,
        measureTypeAddContext.rowIndex,
        'measureType',
        label
      );
      setMeasureTypeAddContext(null);
    };

    const additionalFields = (
      pipe: PipeSize1,
      index: number,
      helpers?: {
        handleUnitChange: (
          index: number,
          field: 'pipeSize' | 'wallThickness' | 'length' | 'outputAssemblyLength',
          value: string
        ) => void;
        UNIT_OPTIONS: { value: string; label: string }[];
        defaultUnit?: string;
        canEdit?: boolean;
        showValidation?: boolean;
        validateField?: (value: any) => boolean;
      }
    ) => (
      <>
        <div data-field="number_of_spools">
          <Label htmlFor={`numberOfSpools-${index}`}>
            Number of Spools <span className="text-red-500">*</span>
          </Label>
          <NumberInput
            id={`numberOfSpools-${index}`}
            min={0}
            step={1}
            value={getNumberOfSpoolsFromPipe(pipe)}
            onChange={(value) => handleNumberOfSpoolsChange(value, index)}
            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
              if (!canEdit) return;
              const value = e.target.value;
              const num = parseInt(value) || 0;
              const currentPipe = state.pipeSizes[index];
              const hasProduction = (currentPipe as any).productionData?.isProductionAvailable;

              if (
                hasProduction &&
                currentPipe.productionData?.numberOfProductions &&
                currentPipe.productionData.numberOfProductions > num
              ) {
                setPendingSpoolsChange({
                  value: currentPipe.productionData.numberOfProductions.toString(),
                  index,
                });
                setIsSpoolsModalVisible(true);
                const forced =
                  currentPipe.productionData?.numberOfProductions ||
                  getNumberOfSpoolsFromPipe(currentPipe);
                setState((prev) => ({
                  ...prev,
                  pipeSizes: prev.pipeSizes.map((p, i) =>
                    i === index
                      ? {
                          ...withSyncedSpoolCounts(p, forced),
                          spoolDetails: buildSpoolDetailsPayload(
                            forced,
                            normalizeSpoolDetailsRows(forced, getSpoolDetailsFromPipe(p))
                          ),
                        }
                      : p
                  ),
                }));
              }
            }}
            className={`mt-2 ${
              showValidation && !validateField(getNumberOfSpoolsFromPipe(pipe))
                ? 'border-red-500'
                : ''
            }`}
            placeholder="Enter number of spools"
            disabled={!canEdit}
          />
        </div>

        {getNumberOfSpoolsFromPipe(pipe) > 0 && (
          <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50/80 p-3 space-y-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Spool details
              </p>
            </div>
            <div className="space-y-2 max-h-[min(420px,50vh)] overflow-y-auto pr-1">
              {normalizeSpoolDetailsRows(
                getNumberOfSpoolsFromPipe(pipe),
                getSpoolDetailsFromPipe(pipe)
              ).map((row, rowIndex) => (
                <div
                  key={`${index}-spool-${rowIndex}`}
                  className="rounded-md border border-gray-200 bg-white p-3 shadow-sm"
                >
                  <p className="text-[11px] font-semibold text-gray-500 mb-2">
                    Spool {rowIndex + 1}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-2 mb-2">
                    <div>
                      <Label className="text-xs text-gray-600">Name</Label>
                      <Input
                        className="mt-1 h-10 text-sm"
                        value={
                          row.spoolName?.trim()
                            ? row.spoolName
                            : defaultSpoolDisplayName(rowIndex)
                        }
                        onChange={(e) =>
                          updateSpoolDetailCell(index, rowIndex, 'spoolName', e.target.value)
                        }
                        placeholder={defaultSpoolDisplayName(rowIndex)}
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Spool Type</Label>
                      <Input
                        className="mt-1 h-10 text-sm"
                        value={row.spoolType ?? ''}
                        onChange={(e) =>
                          updateSpoolDetailCell(index, rowIndex, 'spoolType', e.target.value)
                        }
                        placeholder="e.g. Prefab"
                        disabled={!canEdit}
                      />
                    </div>
                    <div>
                        <Label htmlFor={`spoolLength-${index}-${rowIndex}`} className="text-xs text-gray-600">
                          Spool length
                        </Label>
                      <NumberInput
                        id={`spoolLength-${index}-${rowIndex}`}
                        min={0}
                        step={0.01}
                        value={spoolLengthAsNumberInputValue(row.spoolLength)}
                        onChange={(value) =>
                          updateSpoolDetailCell(
                            index,
                            rowIndex,
                            'spoolLength',
                            value === '' || value == null ? '' : String(value)
                          )
                        }
                        showUnitSelect
                        unitValue={pipe.units?.length || unit || helpers?.defaultUnit || 'in'}
                        unitOptions={helpers?.UNIT_OPTIONS ?? []}
                        onUnitChange={(v) => helpers?.handleUnitChange(index, 'length', v)}
                        unitSelectWrapperClassName="mt-1"
                       
                        placeholder="Enter spool length"
                        disabled={!canEdit}
                      />
                    </div>
                    <div className="space-y-2">
                      <div>
                        <Label
                          htmlFor={`measuretype-${index}-${rowIndex}`}
                          className="text-xs text-gray-600"
                        >
                           Length Measurement Type
                        </Label>
                      </div>
                      <div className="relative mt-1">
                        <SearchableSelect
                          value={
                            row.measureType && String(row.measureType).trim() !== ''
                              ? String(row.measureType).trim()
                              : 'N/A'
                          }
                          onValueChange={(value: string) => {
                            updateSpoolDetailCell(index, rowIndex, 'measureType', value);
                          }}
                          placeholder="Select Length Measurement Type"
                          searchPlaceholder="Search measurement types..."
                          options={buildMeasureTypeSelectOptions(pipe, spoolLengthMeasurementTags)}
                          onAddNew={() => {
                            setMeasureTypeAddContext({ pipeIndex: index, rowIndex });
                            setIsAddingMeasureType(true);
                          }}
                          addNewText="Add new Length Measurement Type"
                          showAddButton={canEdit}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-gray-600">Number of Welds</Label>
                      <Input
                        className="mt-1 h-10 text-sm"
                        value={row.welds ?? ''}
                        onChange={(e) =>
                          updateSpoolDetailCell(index, rowIndex, 'welds', e.target.value)
                        }
                        placeholder="e.g. 4"
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      </>
    );

    return (
      <>
        <CommonSetupForm
          project={project}
          state={state}
          setState={setState}
          title="Spools"
          icon={<Layers className="h-5 w-5" />}
          iconColor="text-blue-700"
          additionalFields={additionalFields as any}
          manufacturingList={manufacturingList}
          setManufacturingList={setManufacturingList}
          unit={unit}
          setUnit={setUnit}
          showValidation={showValidation}
          setHasUnsavedChanges={setHasUnsavedChanges}
          onSavePipe={onSavePipe}
          canEdit={canEdit}
        />

        <AddNewItemDialog
          open={isAddingMeasureType}
          onOpenChange={(open) => {
            setIsAddingMeasureType(open);
            if (!open) setMeasureTypeAddContext(null);
          }}
          title="Add spool length measurement type"
          label="Measurement type name"
          placeholder="Enter a label (e.g. developed length, centreline)"
          onAdd={handleAddMeasureType}
          buttonText="Add measurement type"
          cancelText="Cancel"
        />

        {/* Spools Reduction Confirmation Dialog */}
        <Dialog
          open={isSpoolsModalVisible}
          onOpenChange={() => handleCancelSpoolsChange()}
        >
          <DialogContent className="max-w-[700px] p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                <Trash2 className="h-5 w-5 text-red-600" />
              </div>
              <DialogTitle className="text-xl font-semibold">Reduce Spools</DialogTitle>
            </div>

            {(() => {
              if (!pendingSpoolsChange) return null;
              const currentPipe = state.pipeSizes[pendingSpoolsChange.index];

              return (
                <div>
                  <div className="mb-3">
                    <p className="text-600 mb-2">
                      ⚠️ Cannot decrease the number of spools for{' '}
                      <span className="font-semibold text-red-600">{currentPipe.pipeName}</span>{' '}
                      because the spools have associated production data.
                    </p>
                  </div>
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-sm text-yellow-700">
                      <span className="font-semibold">Action Required:</span>
                      <br />
                      <span className="block mt-1">
                        • Please remove all associated production data before decreasing the number
                        of spools.
                      </span>
                    </p>
                  </div>
                </div>
              );
            })()}

            <DialogFooter className="gap-3">
              <Button variant="outline" onClick={handleCancelSpoolsChange}>
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }
);
